import React from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Paginator } from 'primereact/paginator'
import { Chip } from 'primereact/chip'
import { DataView } from 'primereact/dataview'
import { fetchRecentObituaries, Obituary } from '../api/obituaries'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/store'
import { setLanguage } from '../store/slices/languageSlice'
import { toggleMode } from '../store/slices/themeSlice'
import ClassicAppBar from '../components/ClassicAppBar'

const ObituariesPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light')
  const [items, setItems] = React.useState<Obituary[]>([])
  const [loading, setLoading] = React.useState(true)
  const [first, setFirst] = React.useState(0)
  const [rows, setRows] = React.useState(12)
  const [q, setQ] = React.useState('')
  const [selectedCountry, setSelectedCountry] = React.useState<string>('')
  const navigate = useNavigate()

  React.useEffect(() => {
    const loadObituaries = async () => {
      try {
        setLoading(true)
        const data = await fetchRecentObituaries(100)
        setItems(data)
      } catch (error) {
        console.error('Failed to load obituaries:', error)
        setItems([])
      } finally {
        setLoading(false)
      }
    }
    loadObituaries()
  }, [])

  const filteredItems = React.useMemo(() => {
    let filtered = items

    if (q.trim()) {
      const query = q.toLowerCase()
      filtered = filtered.filter(x =>
        x.name.toLowerCase().includes(query) ||
        x.nameTamil?.includes(q) ||
        x.residence?.toLowerCase().includes(query)
      )
    }

    if (selectedCountry) {
      filtered = filtered.filter(x => x.country === selectedCountry)
    }

    return filtered
  }, [items, q, selectedCountry])

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  const onPageChange = (event: { first: number, rows: number }) => {
    setFirst(event.first)
    setRows(event.rows)
  }

  const itemTemplate = (o: Obituary) => {
    return (
      <div className="col-12 mb-6">
        <div
          className="flex flex-column md:flex-row p-5 w-full border-round-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(211, 84, 0, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)'
            e.currentTarget.style.boxShadow = '0 16px 48px rgba(211, 84, 0, 0.2)'
            e.currentTarget.style.border = '2px solid rgba(211, 84, 0, 0.3)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)'
            e.currentTarget.style.border = '2px solid rgba(211, 84, 0, 0.15)'
          }}
        >
          {/* Avatar with gradient border - Name label removed */}
          <div className="flex-shrink-0 mb-4 md:mb-0 mr-0 md:mr-8 mx-auto md:mx-0">
            <div
              className="border-circle overflow-hidden relative"
              style={{
                width: '12rem',
                height: '12rem',
                padding: '5px',
                background: 'linear-gradient(135deg, #d35400 0%, #e67e22 50%, #d35400 100%)',
                boxShadow: '0 8px 24px rgba(211, 84, 0, 0.25)'
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop"
                alt={o.name}
                className="w-full h-full object-cover border-circle"
                style={{ border: '4px solid white' }}
              />
            </div>
          </div>

          {/* Content Section - Profile Style */}
          <div className="flex-1 flex flex-column justify-content-center text-center md:text-left">
            {/* Header: Name and Dates */}
            <div className="mb-4 border-bottom-1 border-gray-200 pb-3">
              <div className="flex flex-column md:flex-row justify-content-between align-items-center md:align-items-start mb-2">
                {/* Name */}
                <h2 className="text-4xl font-bold font-serif m-0 mb-2 md:mb-0" style={{
                  background: 'linear-gradient(135deg, #1f2937 0%, #d35400 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: '1.2'
                }}>
                  {((i18n.language === 'ta' && o.nameTamil) ? o.nameTamil : (i18n.language === 'de' && o.nameGerman) ? o.nameGerman : o.name) || 'Max Mustermann'}
                </h2>

                {/* Published/Type Chips */}
                <div className="flex gap-2">
                  <Chip
                    label={i18n.language === 'ta' ? o.typeTamil : i18n.language === 'de' ? o.typeGerman : o.type}
                    className="text-xs font-bold bg-orange-50 text-orange-700"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="text-xl font-medium flex align-items-center justify-content-center md:justify-content-start gap-3" style={{ color: '#374151' }}>
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-star text-orange-500 text-lg"></i>
                  <span className="font-bold">{o.birthDate || '01.01.1950'}</span>
                </span>
                <span className="mx-2" style={{ color: '#9ca3af' }}>|</span>
                <span className="flex align-items-center gap-2">
                  <i className="text-2xl font-bold text-gray-600">†</i>
                  <span className="font-bold">{o.deathDate || '14.02.2024'}</span>
                </span>
                <span className="text-lg ml-3 font-medium" style={{ color: '#4b5563' }}>
                  ({o.age || 74} Years)
                </span>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid mb-4">
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2 p-3 border-round-xl bg-gray-50 hover:bg-white transition-colors transition-duration-300 h-full justify-content-center" style={{ border: '1px solid rgba(0,0,0,0.08)', minHeight: '80px' }}>
                  <span className="text-xs text-600 uppercase tracking-wide font-bold flex align-items-center gap-2">
                    <i className="pi pi-map-marker text-orange-600 text-sm"></i> Birth Place
                  </span>
                  <span className="font-bold text-900 text-lg line-height-3">
                    {i18n.language === 'ta' ? o.birthPlaceTamil : i18n.language === 'de' ? o.birthPlaceGerman : o.birthPlace}
                  </span>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2 p-3 border-round-xl bg-gray-50 hover:bg-white transition-colors transition-duration-300 h-full justify-content-center" style={{ border: '1px solid rgba(0,0,0,0.08)', minHeight: '80px' }}>
                  <span className="text-xs text-600 uppercase tracking-wide font-bold flex align-items-center gap-2">
                    <i className="pi pi-home text-orange-600 text-sm"></i> Residence
                  </span>
                  <span className="font-bold text-900 text-lg line-height-3">
                    {i18n.language === 'ta' ? o.residenceTamil : i18n.language === 'de' ? o.residenceGerman : o.residence}
                  </span>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2 p-3 border-round-xl bg-gray-50 hover:bg-white transition-colors transition-duration-300 h-full justify-content-center" style={{ border: '1px solid rgba(0,0,0,0.08)', minHeight: '80px' }}>
                  <span className="text-xs text-600 uppercase tracking-wide font-bold flex align-items-center gap-2">
                    <i className="pi pi-compass text-orange-600 text-sm"></i> Religion
                  </span>
                  <span className="font-bold text-900 text-lg flex align-items-center gap-2 line-height-3">
                    {o.religion?.toLowerCase().includes('hindu') && <span className="text-xl" title="Hindu">🕉️</span>}
                    {(o.religion?.toLowerCase().includes('christian') || o.religion?.toLowerCase().includes('katholisch') || o.religion?.toLowerCase().includes('evangelisch')) && <span className="text-xl" title="Christian">✝️</span>}
                    {(o.religion?.toLowerCase().includes('muslim') || o.religion?.toLowerCase().includes('islam')) && <span className="text-xl" title="Muslim">☪️</span>}
                    {o.religion?.toLowerCase().includes('buddhist') && <span className="text-xl" title="Buddhist">☸️</span>}
                    <span>{o.religion || '-'}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Content Preview */}
            <p className="text-700 line-height-3 mb-4 text-lg font-italic border-left-3 border-orange-200 pl-4 py-1">
              "{i18n.language === 'ta' ? o.contentTamil : i18n.language === 'de' ? o.contentGerman : o.content}"
            </p>

            {/* Action buttons */}
            <div className="flex gap-3 justify-content-center md:justify-content-start flex-wrap mb-2">
              <Button
                label="Tribute Now"
                icon="pi pi-heart"
                className="font-bold"
                style={{
                  background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                  border: 'none',
                  borderRadius: '2rem',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  boxShadow: '0 6px 20px rgba(211, 84, 0, 0.35)',
                  transition: 'all 0.3s ease',
                  gap: '0.5rem'
                }}
                onClick={() => navigate(`/obituaries/${o.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(211, 84, 0, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)'
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(211, 84, 0, 0.35)'
                }}
              />
              <Button
                label="Send Flowers"
                icon="pi pi-gift"
                className="font-bold"
                style={{
                  background: 'white',
                  border: '2px solid #d35400',
                  color: '#d35400',
                  borderRadius: '2rem',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(211, 84, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(211, 84, 0, 0.1), rgba(230, 126, 34, 0.1))'
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(211, 84, 0, 0.25)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white'
                  e.currentTarget.style.transform = 'translateY(0) scale(1)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 84, 0, 0.15)'
                }}
              />
              <Button
                label="Share"
                icon="pi pi-share-alt"
                className="font-bold"
                style={{
                  background: 'rgba(211, 84, 0, 0.05)',
                  border: '1px solid rgba(211, 84, 0, 0.2)',
                  color: '#d35400',
                  borderRadius: '2rem',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(211, 84, 0, 0.15)'
                  e.currentTarget.style.border = '1px solid rgba(211, 84, 0, 0.3)'
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(211, 84, 0, 0.2)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(211, 84, 0, 0.05)'
                  e.currentTarget.style.border = '1px solid rgba(211, 84, 0, 0.2)'
                  e.currentTarget.style.transform = 'translateY(0) scale(1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Tribute count */}
            {(o.tributes || 0) > 0 && (
              <div
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl"
                style={{
                  background: 'rgba(211, 84, 0, 0.1)',
                  border: '1px solid rgba(211, 84, 0, 0.2)'
                }}
              >
                <i className="pi pi-users text-orange-600"></i>
                <span className="font-bold text-orange-600 text-sm">
                  {o.tributes || 0} people tributed
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-column bg-gray-50">
      <Helmet>
        <title>{t('site.obituaries')} · YarlHub</title>
        <meta name="description" content="Recent Tamil obituaries" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : undefined} />
      </Helmet>

      <ClassicAppBar
        mode={themeMode}
        onToggleMode={handleToggleMode}
        onChangeLang={handleLanguageChange}
      />

      <div className="w-full max-w-7xl mx-auto px-4 lg:px-6">
        {loading ? (
          <div className="flex justify-content-center p-8">
            <div className="text-center">
              <i className="pi pi-spin pi-spinner text-orange-500 text-4xl mb-3"></i>
              <div className="text-orange-500 text-xl font-semibold">{t('loading')}...</div>
            </div>
          </div>
        ) : (
          <>
            {/* Header Section */}
            <div className="mb-5">
              <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-3">
                <div className="flex align-items-center gap-3">
                  <i className="pi pi-users text-4xl text-orange-600"></i>
                  <h1 className="text-4xl md:text-5xl font-bold font-serif text-900 m-0">
                    {t('site.obituaries')}
                  </h1>
                </div>

                <span className="p-input-icon-left" style={{ width: '100%', maxWidth: '400px' }}>
                  <i className="pi pi-search" style={{
                    left: '1.25rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(211, 84, 0, 0.6)'
                  }} />
                  <InputText
                    value={q}
                    onChange={(e) => { setFirst(0); setQ(e.target.value) }}
                    placeholder={t('placeholder.search') || 'Search obituaries...'}
                    className="w-full"
                    style={{
                      borderRadius: '2rem',
                      paddingLeft: '3rem',
                      paddingRight: '1.5rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid rgba(211, 84, 0, 0.2)',
                      background: 'rgba(255, 255, 255, 0.9)'
                    }}
                  />
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-content-end mb-5">
              <Button
                label="Submit Obituary"
                icon="pi pi-plus"
                className="font-bold"
                style={{
                  background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                  border: 'none',
                  borderRadius: '2rem',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(211, 84, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(211, 84, 0, 0.4)'
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 84, 0, 0.3)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              />
            </div>

            <div className="grid">
              {/* Main Feed */}
              <div className="col-12 lg:col-8">
                <h2 className="text-3xl font-bold font-serif text-900 mb-4">Recent Posts</h2>

                <DataView
                  value={filteredItems.slice(first, first + rows)}
                  itemTemplate={itemTemplate}
                  paginator={false}
                  className="w-full"
                  pt={{
                    content: { className: 'bg-transparent border-none p-0' },
                    header: { className: 'bg-transparent border-none' }
                  }}
                />

                <Paginator
                  first={first}
                  rows={rows}
                  totalRecords={filteredItems.length}
                  onPageChange={onPageChange}
                  className="mt-4 border-none bg-transparent"
                />
              </div>

              {/* Sidebar */}
              <div className="col-12 lg:col-4 lg:pl-8 mt-6 lg:mt-0">
                <div className="sticky top-0" style={{ top: '100px' }}>
                  {/* Recent Tributes */}
                  <div
                    className="border-round-3xl overflow-hidden mb-4"
                    style={{
                      background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(211, 84, 0, 0.15)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                    }}
                  >
                    {/* Header with gradient */}
                    <div
                      className="p-4"
                      style={{
                        background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="flex align-items-center gap-2">
                        <i className="pi pi-heart-fill text-white text-xl"></i>
                        <h3 className="text-xl font-bold text-white m-0">Recent Tributes</h3>
                      </div>
                    </div>

                    {/* Tribute list */}
                    <div className="p-4">
                      <div className="flex flex-column gap-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="flex align-items-center gap-3 p-3 border-round-2xl cursor-pointer"
                            style={{
                              background: 'rgba(255, 255, 255, 0.6)',
                              border: '1px solid rgba(211, 84, 0, 0.1)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(211, 84, 0, 0.05)'
                              e.currentTarget.style.transform = 'translateX(8px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 84, 0, 0.15)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)'
                              e.currentTarget.style.transform = 'translateX(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              className="flex-shrink-0 border-circle flex align-items-center justify-content-center"
                              style={{
                                width: '3rem',
                                height: '3rem',
                                background: `linear-gradient(135deg, rgba(211, 84, 0, ${0.2 + i * 0.1}), rgba(230, 126, 34, ${0.3 + i * 0.1}))`,
                                border: '2px solid rgba(211, 84, 0, 0.3)'
                              }}
                            >
                              <i className="pi pi-user text-orange-600 text-lg"></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="font-bold text-sm mb-1" style={{ color: '#111827' }}>Sample Name {i}</div>
                              <div className="text-xs flex align-items-center gap-1" style={{ color: '#4b5563' }}>
                                <i className="pi pi-clock text-orange-500" style={{ fontSize: '0.7rem' }}></i>
                                {i} hours ago
                              </div>
                            </div>
                            <i className="pi pi-angle-right text-orange-500"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Countries Filter */}
                  <div
                    className="border-round-3xl overflow-hidden"
                    style={{
                      background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(211, 84, 0, 0.15)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                    }}
                  >
                    {/* Header with gradient */}
                    <div
                      className="p-4"
                      style={{
                        background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="flex align-items-center gap-2">
                        <i className="pi pi-globe text-white text-xl"></i>
                        <h3 className="text-xl font-bold text-white m-0">Browse by Country</h3>
                      </div>
                    </div>

                    {/* Country list */}
                    <div className="p-4">
                      <div className="flex flex-column gap-2">
                        <div
                          className="flex align-items-center gap-3 p-3 border-round-2xl cursor-pointer"
                          style={{
                            background: selectedCountry === '' ? 'rgba(211, 84, 0, 0.1)' : 'rgba(255, 255, 255, 0.6)',
                            border: selectedCountry === '' ? '2px solid rgba(211, 84, 0, 0.3)' : '1px solid rgba(211, 84, 0, 0.1)',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => setSelectedCountry('')}
                          onMouseEnter={(e) => {
                            if (selectedCountry !== '') {
                              e.currentTarget.style.background = 'rgba(211, 84, 0, 0.05)'
                              e.currentTarget.style.transform = 'translateX(5px)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedCountry !== '') {
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)'
                              e.currentTarget.style.transform = 'translateX(0)'
                            }
                          }}
                        >
                          <span style={{ fontSize: '1.5rem' }}>🌍</span>
                          <span className={`flex-grow-1 ${selectedCountry === '' ? 'font-bold text-orange-600' : 'font-medium'}`} style={{ color: selectedCountry === '' ? '' : '#111827' }}>
                            All Countries
                          </span>
                          {selectedCountry === '' && <i className="pi pi-check-circle text-orange-600"></i>}
                        </div>

                        {[
                          { name: 'Sri Lanka', flag: '🇱🇰' },
                          { name: 'India', flag: '🇮🇳' },
                          { name: 'Germany', flag: '🇩🇪' },
                          { name: 'UK', flag: '🇬🇧' },
                          { name: 'Canada', flag: '🇨🇦' },
                          { name: 'France', flag: '🇫🇷' },
                          { name: 'Switzerland', flag: '🇨🇭' }
                        ].map(country => (
                          <div
                            key={country.name}
                            className="flex align-items-center gap-3 p-3 border-round-2xl cursor-pointer"
                            style={{
                              background: selectedCountry === country.name ? 'rgba(211, 84, 0, 0.1)' : 'rgba(255, 255, 255, 0.6)',
                              border: selectedCountry === country.name ? '2px solid rgba(211, 84, 0, 0.3)' : '1px solid rgba(211, 84, 0, 0.1)',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => setSelectedCountry(country.name)}
                            onMouseEnter={(e) => {
                              if (selectedCountry !== country.name) {
                                e.currentTarget.style.background = 'rgba(211, 84, 0, 0.05)'
                                e.currentTarget.style.transform = 'translateX(5px)'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedCountry !== country.name) {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)'
                                e.currentTarget.style.transform = 'translateX(0)'
                              }
                            }}
                          >
                            <span style={{ fontSize: '1.5rem' }}>{country.flag}</span>
                            <span className={`flex-grow-1 ${selectedCountry === country.name ? 'font-bold text-orange-600' : 'font-medium'}`} style={{ color: selectedCountry === country.name ? '' : '#111827' }}>
                              {country.name}
                            </span>
                            {selectedCountry === country.name && <i className="pi pi-check-circle text-orange-600"></i>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ObituariesPage
